/**
 * Majalisna API Configuration
 * 
 * This file contains all API endpoint configurations.
 * Centralizing API endpoints here makes it easier to modify them if needed.
 */

// Base API URL - change this based on environment
const BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' 
    ? 'https://majalisna-61c919e79d71.herokuapp.com/api/v1' 
    : 'http://localhost:8001/api/v1');

const API = {
  BASE_URL: BASE_URL,

  // Authentication endpoints
  auth: {
    login: `${BASE_URL}/auth/login`,
    logout: `${BASE_URL}/auth/logout`,
    register: `${BASE_URL}/auth/register`,
    resetPassword: `${BASE_URL}/auth/reset-password`,
    forgotPassword: `${BASE_URL}/auth/forgot-password`,
    refreshToken: `${BASE_URL}/auth/refresh-token`,
    me: `${BASE_URL}/auth/me`,
  },
  
  // User management endpoints
  users: {
    base: `${BASE_URL}/users`,
    getById: (id) => `${BASE_URL}/users/${id}`,
    update: (id) => `${BASE_URL}/users/${id}`,
    delete: (id) => `${BASE_URL}/users/${id}`,
    changeRole: (id) => `${BASE_URL}/users/${id}/role`,
    committee: {
      base: `${BASE_URL}/users/committee`,
      getById: (id) => `${BASE_URL}/users/committee/${id}`,
    },
  },
  
  // Organization structure endpoints
  organization: {
    headquarters: `${BASE_URL}/organization/headquarters`,
    branches: {
      base: `${BASE_URL}/organization/branches`,
      getById: (id) => `${BASE_URL}/organization/branches/${id}`,
    },
    centers: {
      base: `${BASE_URL}/organization/centers`,
      getById: (id) => `${BASE_URL}/organization/centers/${id}`,
      getByBranch: (branchId) => `${BASE_URL}/organization/branches/${branchId}/centers`,
    },
  },
  
  // Nomination endpoints (RENAMED FROM 'applications')
  nominations: {
    stats: `${BASE_URL}/nominations-stats/dashboard/stats`,
    base: `${BASE_URL}/nominations`,
    createDraft: `${BASE_URL}/nominations/draft`, // <-- NEW
    getById: (id) => `${BASE_URL}/nominations/${id}`,
    update: (id) => `${BASE_URL}/nominations/${id}`,
    submit: `${BASE_URL}/nominations/submit`, // تعديل لتكون نقطة نهاية مباشرة بدون الحاجة إلى معرف
    delete: (id) => `${BASE_URL}/nominations/${id}`,
    evaluationsExists: (id) => `${BASE_URL}/nominations/${id}/evaluations/exists`,
    checkStatus: (id, nationalId) => `${BASE_URL}/nominations/status/${id}?national_id=${nationalId}`,
    checkExisting: (nationalId) => `${BASE_URL}/nominations/check-existing/${nationalId}`,
    // 'saveProgress' is removed in favor of updating the draft directly
  },

  // File Upload endpoints (S3 Direct Upload)
  fileUpload: {
    requestPutUploadUrl: (nominationId) => `${BASE_URL}/files/nominations/${nominationId}/request-put-upload-url`,
    confirmUpload: (fileId) => `${BASE_URL}/files/${fileId}/confirm-upload`,
  },
  
  // Files API endpoints for server-side upload
  files: {
    // Server-side upload endpoints
    serverUpload: `${BASE_URL}/files/server-upload`,
    batchUpload: `${BASE_URL}/files/batch-upload`,
    // Deferred upload endpoints (new)
    requestDeferredUploadUrl: `${BASE_URL}/files/request-upload-url`,
    confirmDeferredUpload: (fileId) => `${BASE_URL}/files/confirm-upload/${fileId}`,
    // Legacy client-side upload endpoints (will be deprecated)
    requestUploadUrl: `${BASE_URL}/files/request-upload-url`,
    confirmUpload: `${BASE_URL}/files/confirm-upload`,
  },
  
  // Document endpoints (Legacy, can be removed later if not used)
  documents: {
    base: `${BASE_URL}/documents`,
    getById: (id) => `${BASE_URL}/documents/${id}`,
    delete: (id) => `${BASE_URL}/documents/${id}`,
    getByNomination: (nominationId) => `${BASE_URL}/nominations/${nominationId}/documents`,
  },
  
  // Evaluation endpoints
  evaluations: {
    base: `${BASE_URL}/evaluations`,
    getById: (id) => `${BASE_URL}/evaluations/${id}`,
    create: `${BASE_URL}/evaluations`,
    update: (id) => `${BASE_URL}/evaluations/${id}`,
    delete: (id) => `${BASE_URL}/evaluations/${id}`,
    getByNomination: (nominationId) => `${BASE_URL}/nominations/${nominationId}/evaluations`,
    getByCommittee: (committeeId) => `${BASE_URL}/committee/${committeeId}/evaluations`,
  },
  
  // Profile generation endpoints (renamed from candidates to align with nominations)
  profiles: {
    generate: (nominationId) => `${BASE_URL}/nominations/${nominationId}/profile`,
    pdf: (nominationId) => `${BASE_URL}/nominations/${nominationId}/profile/pdf`,
  },
  
  // Reports endpoints
  reports: {
    nomination: (nominationId) => `${BASE_URL}/reports/nomination/${nominationId}/report`,
    bulkExportReports: `${BASE_URL}/reports/bulk-export/reports`,
  },
  
  // Members endpoints
  members: {
    base: `${BASE_URL}/members`,
    getById: (id) => `${BASE_URL}/members/${id}`,
    update: (id) => `${BASE_URL}/members/${id}`,
    delete: (id) => `${BASE_URL}/members/${id}`,
    checkMembership: (nationalId) => `${BASE_URL}/members/check/${nationalId}`,
  },
  
  // Notification endpoints
  notifications: {
    sendSMS: `${BASE_URL}/notifications/send-sms`,
    sendOTP: `${BASE_URL}/notifications/send-otp`,
    verifyOTP: `${BASE_URL}/notifications/verify-otp`,
    applicationConfirmation: `${BASE_URL}/notifications/application-confirmation`,
  },
  
  // Dashboard endpoints
  dashboard: {
    stats: `${BASE_URL}/nomination-stats/dashboard/stats`,
  },
  
  // Settings endpoints
  settings: {
    base: `${BASE_URL}/settings`,
    nominationStatus: `${BASE_URL}/settings/nomination-status`,
    getByKey: (key) => `${BASE_URL}/settings/${key}`,
    update: (key) => `${BASE_URL}/settings/${key}`,
    delete: (key) => `${BASE_URL}/settings/${key}`,
  },
  
  // Committee endpoints
  committee: {
    dashboard: {
      // Use the standard evaluations endpoint - committee role filtering is done on the backend
      get: `${BASE_URL}/evaluations`,
      stats: `${BASE_URL}/nomination-stats/dashboard/stats`,
    },
    candidates: {
      // Use nominations endpoint with committee role filtering on the backend
      list: `${BASE_URL}/nominations`,
      getById: (id) => `${BASE_URL}/nominations/${id}`,
    },
    evaluations: {
      create: `${BASE_URL}/evaluations`,
      update: (id) => `${BASE_URL}/evaluations/${id}`,
      getById: (id) => `${BASE_URL}/evaluations/${id}`,
      getByNomination: (nominationId) => `${BASE_URL}/evaluations/nomination/${nominationId}`,
      // Profile download endpoint
      downloadProfile: (nominationId) => `${BASE_URL}/nominations/${nominationId}/profile`,
    },
  },
};

export default API;